import type { AdminPermission } from '~/models/admin-role';

interface PagePermission {
  /** Any one of these keys opens the page. */
  read: AdminPermission[];
  /** Key for create and update actions on the page. */
  write?: AdminPermission;
  /** Key for delete actions. Without it, `write` applies. */
  delete?: AdminPermission;
}

const pagePermission = (
  read: AdminPermission | AdminPermission[],
  write?: AdminPermission,
  del?: AdminPermission,
): PagePermission => ({ read: Array.isArray(read) ? read : [read], write, delete: del });

const reports = pagePermission('report:read', 'report:write');
const transactions = pagePermission('transaction:read', 'transaction:write');
const shopOperation = pagePermission('shop_operation:read', 'shop_operation:write');
const roles = pagePermission('role:read', 'role:write', 'role:delete');
const systemBase = pagePermission('system:base', 'system:base');
const systemTechnical = pagePermission('system:technical', 'system:technical');

/**
 * Keys that each page needs. The backend checks the same keys on its API (see
 * RequireAdminPermission in pos-backend). A page that is not in this list needs no key.
 * The longest matching path wins, so `/local-servers/configurations` overrides `/local-servers`.
 */
const PAGE_PERMISSIONS: Record<string, PagePermission> = {
  '/system-dashboard': systemBase,
  '/organizations': systemBase,
  '/local-servers': systemBase,
  '/local-servers/configurations': systemTechnical,
  '/app-configurations': systemTechnical,
  '/system-properties': systemTechnical,
  '/ip-whitelists': systemTechnical,
  '/external-authorizations': systemTechnical,
  '/announcements/compose': systemBase,

  '/reports': reports,
  '/report-dashboard': reports,
  '/report-trend': reports,
  '/report-daily': reports,
  '/report-sales': reports,
  '/report-dayparting': reports,
  '/report-categories': reports,
  '/report-menu-items': reports,
  '/report-void': reports,
  '/report-operators': reports,
  '/report-payments': reports,
  '/report-customers': reports,
  '/report-general': reports,
  '/report-ranking': reports,
  '/receipts': transactions,
  '/invoices': transactions,
  '/hms-receipt-payments': transactions,
  '/hms-rooms': transactions,
  '/hms-receipt-imports': transactions,

  '/shops': pagePermission('shop_config:read', 'shop_config:write'),
  '/operators': pagePermission('employee:read', 'employee:write', 'employee:delete'),
  '/roles': roles,
  '/admin-roles': roles,
  '/admin-audit-logs': pagePermission('audit_log:read'),
  '/integrations': pagePermission('integration:write', 'integration:write'),

  '/outlets': shopOperation,
  '/revenue-centers': shopOperation,
  '/areas': shopOperation,
  '/modes': shopOperation,
  '/daypartings': shopOperation,
  '/printers': shopOperation,
  '/network-topology': shopOperation,
  '/properties': shopOperation,
  '/budgets': shopOperation,

  '/menu-items': shopOperation,
  '/condiments': shopOperation,
  '/courses': shopOperation,
  '/categories': shopOperation,

  '/order-charges': shopOperation,
  '/table-charges': shopOperation,
  '/payments': shopOperation,
  '/coupons': shopOperation,
  '/discounts': shopOperation,

  '/devices': shopOperation,
  '/table-maps': shopOperation,
  '/key-arrangements': shopOperation,
  '/order-layouts': shopOperation,
  '/order-groups': shopOperation,

  '/table-order-view-settings': shopOperation,
  '/table-order-table-configurations': shopOperation,
  '/table-order-menu-groups': shopOperation,
  '/table-order-menu-subgroups': shopOperation,
  '/table-order-topping-groups': shopOperation,
};

const PAGE_PATHS = Object.keys(PAGE_PERMISSIONS).sort((a, b) => b.length - a.length);

export function pagePermissionFor(path: string): PagePermission | null {
  const match = PAGE_PATHS.find(pagePath => path === pagePath || path.startsWith(`${pagePath}/`));
  return match ? PAGE_PERMISSIONS[match]! : null;
}

/** Pages that the landing page tries, in order. */
export const LANDING_PATHS = ['/report-dashboard', '/receipts', '/menu-items', '/operators', '/announcements'];
